
import { Vue, Component } from 'vue-property-decorator';
import api from '@/api/index';
import $ from 'jquery';
import {
  CardDto,
  OrganizationUnitDto,
  StoreYearlyCardSaleReportDto,
  StoreYearlyCardSaleReportItemDto,
  StoreYearlyCardSaleReportCardItemDto,
  StoreYearlyCardSaleReportMonthlyItemDto
} from '@/api/appService';

interface StoreCardSaleName {
  isFirst: boolean;
  rowspan: number;
  itemName: string;
}

/** 表格行数据：大区 > 片区 > 门店 > 会员卡 */
class StoreCardSaleRow {
  private _names: { [key: number]: StoreCardSaleName } = {};
  private _maxLevel: number = 0;
  private _rowId: string = Math.random().toString();
  mappedTreeItem!: DataTree;
  cardName: string = '';
  month1: number = 0;
  month2: number = 0;
  month3: number = 0;
  month4: number = 0;
  month5: number = 0;
  month6: number = 0;
  month7: number = 0;
  month8: number = 0;
  month9: number = 0;
  month10: number = 0;
  month11: number = 0;
  month12: number = 0;

  get rowId(): string {
    return this._rowId;
  }

  setLevelItem(level: number, name: StoreCardSaleName) {
    this._names[level] = name;
    if (level > this._maxLevel) this._maxLevel = level;
  }

  hasLevel(level: number) {
    return !!this._names[level];
  }

  getItemName(level: number) {
    return this._names[level]?.itemName ?? '';
  }

  getLevel(level: number) {
    return this._names[level];
  }

  getAllNames() {
    return this._names;
  }

  get storeName() {
    return this._names[this._maxLevel]?.itemName ?? '';
  }
}

/** 门店 ID -> 报表项（用于 fetchDetail 后填充树节点） */

class DataTree {
  orgId!: number;
  orgName!: string;
  children?: DataTree[];
  level!: string;
  /** 仅叶子（门店）节点有：该门店下各会员卡的月度数据 */
  cardDataList?: Array<{ cardName: string; monthlyValues: StoreYearlyCardSaleReportMonthlyItemDto[] }>;

  private _yearlyTotal?: number;
  private _rowSpan?: number;

  constructor(orgId: number, orgName: string, level: string) {
    this.orgId = orgId;
    this.orgName = orgName;
    this.level = level;
  }

  get rowSpan(): number {
    if (this._rowSpan !== undefined) return this._rowSpan;

    if (!this.children || this.children.length === 0) {
      // 叶子节点（门店）：返回会员卡数量
      this._rowSpan = (this.cardDataList && this.cardDataList.length)
        ? this.cardDataList.length
        : 1;
    } else {
      // 非叶子节点（大区/片区）：递归累加子节点的 rowSpan
      this._rowSpan = this.children.reduce((sum, child) => sum + child.rowSpan, 0);
    }

    return this._rowSpan;
  }

  get yearlyTotal(): number {
    if (this._yearlyTotal !== undefined) return this._yearlyTotal;
    if (this.cardDataList && this.cardDataList.length) {
      this._yearlyTotal = this.cardDataList.reduce((sum, card) => {
        return sum + (card.monthlyValues || []).reduce((s, m) => s + (m.amount ?? 0), 0);
      }, 0);
    } else {
      this._yearlyTotal = 0;
    }
    return this._yearlyTotal;
  }

  get subYearlyTotal(): number {
    if (!this.children || !this.children.length) return 0;
    return this.children.reduce((sum, c) => sum + c.yearlyTotal, 0);
  }

  clearCache() {
    this._yearlyTotal = undefined;
    this._rowSpan = undefined;
  }
}

@Component
export default class StoreCardSaleYearlyReport extends Vue {
  queryForm: {
    year: number | undefined;
    baseCardId: number | undefined;
  } = {
    year: undefined,
    baseCardId: undefined
  };

  loading = true;
  updating = false;
  baseCardList: CardDto[] = [];
  reportData: StoreYearlyCardSaleReportDto | null = null;
  dataTree: DataTree[] = [];
  /** 门店 ID -> 报表项（用于合计等） */
  orgStoreCardMap: { [storeId: number]: StoreYearlyCardSaleReportItemDto } = {};
  /** 门店 ID -> 树节点（用于 fetchDetail 后填充 cardDataList） */
  storeNodeMap: { [storeId: number]: DataTree } = {};
  rowDataList: StoreCardSaleRow[] = [];

  get ouLevel(): string {
    return (this.$store.getters['user/ou'] as OrganizationUnitDto)?.level ?? '';
  }

  async created() {
    this.queryForm.year = new Date().getFullYear();
    await this.loadBaseCards();
    await this.buildDataTreeStruct();
    this.loading = false;
  }

  mounted() {
    window.addEventListener('resize', this.handleResize);
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize() {
    this.applyFrozenColumns();
  }

  /** 加载会员卡下拉列表 */
  async loadBaseCards() {
    try {
      const list = await api.card.getAllBaseCard();
      this.baseCardList = list || [];
    } catch (e) {
      this.baseCardList = [];
    }
  }

  /** 查询报表 */
  async fetchDetail() {
    this.loading = true;
    try {
      const res = await api.profitReport.getStoreYearlyCardSaleReport({
        year: this.queryForm.year,
        baseCardId: this.queryForm.baseCardId || undefined
      });
      this.reportData = res;
      this.orgStoreCardMap = {};
      (res.items || []).forEach(item => {
        if (item.storeId != null) {
          this.orgStoreCardMap[item.storeId] = item;
          const node = this.storeNodeMap[item.storeId];
          if (node) {
            node.cardDataList = (item.cardSales || []).map(c => ({
              cardName: c.cardName ?? '',
              monthlyValues: (c.monthlyValues || []).slice().sort((a, b) => (a.month ?? 0) - (b.month ?? 0))
            }));
          }
        }
      });
      this.rowDataList = this.buildRows(this.dataTree, 0);
      setTimeout(() => {
        $('#storeCardSaleYearlyReportTable thead tr th.strong').css({ width: '112px' });
        this.applyFrozenColumns();
      }, 1000);
    } finally {
      this.loading = false;
    }
  }

  /** 更新报表 */
  async handleUpdateReport() {
    if (!this.queryForm.baseCardId) {
      this.$message.warning('请先选择会员卡类型');
      return;
    }
    this.updating = true;
    try {
      this.$message.success('报表更新会比较久，请过会儿再来看看最新的数据');
      await api.profitReport.updateStoreYearlyCardSaleReport({
        year: this.queryForm.year,
        month: undefined
      });

    } catch (e) {
      this.$message.error(e?.message || '更新报表失败');
    } finally {
      this.updating = false;
    }
  }

  async handleSearch() {
    if (!this.queryForm.baseCardId) {
      this.$message.warning('请先选择会员卡类型');
      return;
    }
    await this.fetchDetail();
  }

  getAllTotal(): number {
    let result = 0;
    for (const storeId in this.orgStoreCardMap) {
      const item = this.orgStoreCardMap[storeId];
      (item.cardSales || []).forEach(card => {
        (card.monthlyValues || []).forEach(m => {
          result += m.amount ?? 0;
        });
      });
    }
    return result;
  }

  getMonthTotal(month: number): number {
    let result = 0;
    for (const storeId in this.orgStoreCardMap) {
      const item = this.orgStoreCardMap[storeId];
      (item.cardSales || []).forEach(card => {
        const vals = (card.monthlyValues || []).slice().sort((a, b) => (a.month ?? 0) - (b.month ?? 0));
        const m = vals[month - 1];
        if (m) result += m.amount ?? 0;
      });
    }
    return result;
  }

  async buildDataTreeStruct() {
    const ou = this.$store.getters['user/ou'] as OrganizationUnitDto;
    const res = await api.organizationUnit.getOrganizationUnitsOfDescendantBySession({ withMe: false });
    let tree: DataTree[];
    if (ou.level === '总部') {
      tree = this.createDataTree(res, undefined);
    } else {
      tree = this.createDataTree(res, ou.id);
    }
    tree = this.clearDataTree(tree);
    this.dataTree = tree;
  }

  createDataTree(allData: OrganizationUnitDto[], parentId?: number): DataTree[] {
    const children = allData.filter(
      s =>
        s.parentId === parentId ||
        ((parentId == null && (s.parentId == null || s.parentId === undefined)) as boolean)
    );
    if (!children || !children.length) return [];

    return children.map(child => {
      const treeItem = new DataTree(child.id!, child.displayName!, child.level!);
      treeItem.children = this.createDataTree(allData, child.id);
      if (!treeItem.children || !treeItem.children.length) {
        if (treeItem.level === '门店') {
          this.storeNodeMap[child.id!] = treeItem;
          treeItem.cardDataList = [];
        }
      }
      return treeItem;
    });
  }

  clearDataTree(tree: DataTree[]): DataTree[] {
    const newTree: DataTree[] = [];
    if (!tree || !tree.length) return newTree;
    tree.forEach(treeNode => {
      let newTreeNode: DataTree | null = null;
      if (treeNode.children && treeNode.children.length) {
        const subTree = this.clearDataTree(treeNode.children);
        if (subTree.length > 0) {
          newTreeNode = treeNode;
          newTreeNode.children = subTree;
        }
      } else {
        if (treeNode.level === '门店') newTreeNode = treeNode;
      }
      if (newTreeNode) newTree.push(newTreeNode);
    });
    return newTree;
  }


  clearEmptyTree(tree:DataTree[]):DataTree[]{
    if(!tree || !tree.length) return [];


    const nodes = tree.map(treeNode=>{
      const children = this.clearEmptyTree(treeNode.children??[])
      treeNode.children = children;
      return treeNode;
    })
    return nodes;
  }


  /** 构建表格行：大区 > 片区 > 门店 > 会员卡，带合计行 */
  buildRows(
    treeItems: DataTree[],
    level: number
  ): StoreCardSaleRow[] {
    if (!treeItems || !treeItems.length) return [];

    const rows: StoreCardSaleRow[] = [];

    treeItems.forEach(treeItem => {
      treeItem.clearCache();

      if (treeItem.children && treeItem.children.length > 0) {
        // 非叶子节点（大区/片区）：递归处理子节点
        const subRows = this.buildRows(treeItem.children, level + 1);

        if (subRows.length > 0) {
          // 第一行显示当前层级名称（带 rowspan）
          subRows[0].setLevelItem(level, {
            isFirst: true,
            rowspan: treeItem.rowSpan,
            itemName: treeItem.orgName
          });

          // 后续行不显示当前层级（isFirst: false）
          for (let i = 1; i < subRows.length; i++) {
            subRows[i].setLevelItem(level, {
              isFirst: false,
              rowspan: 0,
              itemName: treeItem.orgName
            });
          }

          rows.push(...subRows);
        }
      } else {
        // 叶子节点（门店）：为每张会员卡生成一行
        const cards = treeItem.cardDataList || [];

        if (cards.length === 0) {
          // 没有会员卡数据：生成一行空数据
          const row = new StoreCardSaleRow();
          // 门店层级使用 level，会员卡层级使用 level + 1
          row.setLevelItem(level, {
            isFirst: true,
            rowspan: 1,
            itemName: treeItem.orgName
          });
          row.cardName = '';
          row.mappedTreeItem = treeItem;
          rows.push(row);
        } else {
          // 有会员卡数据：每张卡生成一行
          cards.forEach((cardData, cardIndex) => {
            const row = new StoreCardSaleRow();

            // 设置门店层级（只有第一张卡显示门店名称）
            // 门店层级使用 level，会员卡层级使用 level + 1
            row.setLevelItem(level, {
              isFirst: cardIndex === 0,
              rowspan: cards.length,
              itemName: treeItem.orgName
            });

            // 填充会员卡数据
            row.cardName = cardData.cardName;
            row.mappedTreeItem = treeItem;
            const vals = cardData.monthlyValues || [];
            row.month1 = vals[0]?.amount ?? 0;
            row.month2 = vals[1]?.amount ?? 0;
            row.month3 = vals[2]?.amount ?? 0;
            row.month4 = vals[3]?.amount ?? 0;
            row.month5 = vals[4]?.amount ?? 0;
            row.month6 = vals[5]?.amount ?? 0;
            row.month7 = vals[6]?.amount ?? 0;
            row.month8 = vals[7]?.amount ?? 0;
            row.month9 = vals[8]?.amount ?? 0;
            row.month10 = vals[9]?.amount ?? 0;
            row.month11 = vals[10]?.amount ?? 0;
            row.month12 = vals[11]?.amount ?? 0;

            rows.push(row);
          });
        }
      }
    });

    return rows;
  }

  applyFrozenColumns() {
    const table = $('#storeCardSaleYearlyReportTable');
    if (!table.length) return;

    // 获取所有表头的冻结列，并计算每列的宽度和偏移量
    const headerCells = table.find('thead tr th');
    const columnWidths: number[] = [];
    const columnOffsets: number[] = [];
    const frozenColumnIndices: number[] = [];

    let leftOffset = 0;
    let colIndex = 0;

    headerCells.each((index, cell) => {
      const $cell = $(cell);
      const width = $cell.outerWidth() || 0;
      const isFrozen = $cell.hasClass('frozen-col');

      columnWidths.push(width);
      columnOffsets.push(leftOffset);

      if (isFrozen) {
        frozenColumnIndices.push(colIndex);
        $cell.css({
          left: `${leftOffset}px`
        });
        leftOffset += width;
      }

      colIndex++;
    });

    // 为每一行的 tbody 设置冻结列位置
    // 使用一个二维数组来跟踪每个单元格占据的位置
    const rows = table.find('tbody tr');
    const rowCount = rows.length;
    const colCount = headerCells.length;

    // 创建一个矩阵来跟踪哪些位置已被占据（用于处理 rowspan）
    const cellMatrix: (JQuery | null)[][] = [];
    for (let i = 0; i < rowCount; i++) {
      cellMatrix[i] = new Array(colCount).fill(null);
    }

    // 遍历每一行，填充矩阵
    rows.each((rowIndex, row) => {
      const $row = $(row);
      const cells = $row.find('td');
      let matrixColIndex = 0;

      cells.each((cellIndex, cell) => {
        const $cell = $(cell);

        // 找到下一个未被占据的列位置
        while (matrixColIndex < colCount && cellMatrix[rowIndex][matrixColIndex] !== null) {
          matrixColIndex++;
        }

        if (matrixColIndex >= colCount) return;

        const colspan = parseInt($cell.attr('colspan') || '1', 10);
        const rowspan = parseInt($cell.attr('rowspan') || '1', 10);

        // 在矩阵中标记这个单元格占据的所有位置
        for (let r = 0; r < rowspan; r++) {
          for (let c = 0; c < colspan; c++) {
            if (rowIndex + r < rowCount && matrixColIndex + c < colCount) {
              cellMatrix[rowIndex + r][matrixColIndex + c] = $cell;
            }
          }
        }

        // 如果是冻结列，设置其 left 位置
        if ($cell.hasClass('frozen-col')) {
          if ($cell.hasClass('frozen-col-merged')) {
            // 合并列始终从 0 开始
            $cell.css({
              left: '0px'
            });
          } else {
            // 根据列索引设置正确的 left 值
            const offset = columnOffsets[matrixColIndex] || 0;
            $cell.css({
              left: `${offset}px`
            });
          }
        }

        matrixColIndex += colspan;
      });
    });
  }
}
